import React, { useState, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { resolveUrl } from "./erRendererStyleUtils";

function matchTrim(s) {
	return String(s ?? "").trim();
}
function matchHasText(item) {
	return !!matchTrim(item?.text);
}
function matchHasImage(item) {
	return !!(item?.imageUrl && matchTrim(item.imageUrl));
}
function matchUsesRichLayout(leftItems, rightItems) {
	const all = [...(leftItems || []), ...(rightItems || [])];
	return all.some((it) => matchHasImage(it));
}
/** Primera línea = título; resto = descripción (estilo tarjetas de referencia). */
function splitMatchText(text) {
	const t = matchTrim(text);
	if (!t) return { title: "", subtitle: "" };
	const nl = t.indexOf("\n");
	if (nl === -1) return { title: t, subtitle: "" };
	return { title: t.slice(0, nl).trim(), subtitle: t.slice(nl + 1).trim() };
}
function matchItemFillBg(item) {
	return matchHasText(item) ? "rgba(248, 250, 252, 0.92)" : "transparent";
}
function matchResolvedImg(item) {
	const u = item?.imageUrl && matchTrim(item.imageUrl);
	return u ? resolveUrl(u) : null;
}

/**
 * Chrome (y otros) generan mal la imagen nativa de arrastre bajo ancestros con `transform`
 * (p. ej. el lienzo del escape room con `scale()`): la ficha parece vacía/blanca.
 */
function setPlainTextDragImage(dataTransfer, text, anchorX, anchorY) {
	if (!dataTransfer || typeof document === "undefined") return;
	const el = document.createElement("div");
	el.setAttribute("data-er-drag-ghost", "1");
	el.textContent = String(text ?? "");
	Object.assign(el.style, {
		position: "fixed",
		left: "-10000px",
		top: "0",
		padding: "8px 14px",
		borderRadius: "8px",
		background: "#ffffff",
		color: "#1e293b",
		font: '600 14px system-ui, -apple-system, "Segoe UI", sans-serif',
		border: "1px solid #e2e8f0",
		boxShadow: "0 4px 14px rgba(15, 23, 42, 0.2)",
		maxWidth: "300px",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		pointerEvents: "none",
		zIndex: "2147483645",
	});
	document.body.appendChild(el);
	const ax = Number.isFinite(Number(anchorX)) ? Number(anchorX) : 12;
	const ay = Number.isFinite(Number(anchorY)) ? Number(anchorY) : 12;
	try {
		dataTransfer.setDragImage(el, ax, ay);
	} catch (_) {
		/* noop */
	}
	requestAnimationFrame(() => {
		try {
			if (el.parentNode) el.parentNode.removeChild(el);
		} catch (_) {
			/* noop */
		}
	});
}

/** Busca casilla o pool en la pila de elementos bajo el cursor (no solo el superior). */
function resolveMatchDropTarget(clientX, clientY) {
	if (typeof document === "undefined") return null;
	const stack = document.elementsFromPoint(clientX, clientY);
	for (let i = 0; i < stack.length; i++) {
		const node = stack[i];
		if (!(node instanceof Element)) continue;
		if (node.closest("[data-er-drag-ghost]")) continue;
		const slot = node.closest("[data-match-slot-id]");
		if (slot) {
			const leftId = slot.getAttribute("data-match-slot-id");
			if (leftId) return { kind: "slot", leftId };
		}
		if (node.closest("[data-match-pool]") || node.closest("[data-match-pool-chip]")) {
			return { kind: "pool" };
		}
	}
	return null;
}

function ensureMatchDragStyles() {
	if (typeof document === "undefined") return;
	if (document.getElementById("er-match-drag-styles")) return;
	const el = document.createElement("style");
	el.id = "er-match-drag-styles";
	el.textContent = `
		body[data-er-match-drag] .escape-room-canvas,
		body[data-er-match-drag] .escape-room-canvas * {
			pointer-events: none !important;
		}
		body[data-er-match-drag] [data-er-match-host],
		body[data-er-match-drag] [data-er-match-host] * {
			pointer-events: auto !important;
		}
		body[data-er-match-drag] [data-er-match-host] {
			z-index: 2147483646 !important;
		}
	`;
	document.head.appendChild(el);
}

/** En móvil/tablet el drag HTML5 roba el gesto; usamos solo toque (ficha → casilla). */
function useTouchPreferTapOverDrag() {
	return useMemo(() => {
		if (typeof window === "undefined") return false;
		return window.matchMedia("(pointer: coarse)").matches;
	}, []);
}

export function MatchQuizPlay({ firstQ, onConfirm, done, answer, optionTextStyle }) {
	const intl = useIntl();
	const touchPreferTap = useTouchPreferTapOverDrag();
	const leftItems = firstQ?.matchLeft || [];
	const rightItems = firstQ?.matchRight || [];
	const [assignments, setAssignments] = useState({});
	const [dragOverLeftId, setDragOverLeftId] = useState(null);
	const [isDraggingMatch, setIsDraggingMatch] = useState(false);
	/** Selección táctil: toca ficha → toca casilla (HTML5 DnD falla en muchos móviles). */
	const [tapPickRightId, setTapPickRightId] = useState(null);
	const hasImages = useMemo(() => matchUsesRichLayout(leftItems, rightItems), [leftItems, rightItems]);
	const itemCount = Math.max(leftItems.length, rightItems.length);
	const useImageGrid = hasImages && leftItems.length >= 2;
	/** Texto: grid 2×N dentro del alto fijo del bloque (sin scroll). */
	const useTextPairGrid = !hasImages && itemCount >= 2;
	const useGridLayout = useImageGrid;
	const pairGridRows = Math.max(1, Math.ceil(itemCount / 2));
	const matchDensity = useMemo(() => {
		if (itemCount >= 4) return "compact";
		if (!hasImages && itemCount >= 3) return "compact";
		return "normal";
	}, [hasImages, itemCount]);
	const textUltraCompact = !hasImages && itemCount >= 3;
	const densitySizes =
		textUltraCompact
			? {
				slotMinH: hasImages ? 64 : 0,
				slotImg: 48,
				leftImg: 48,
				poolImg: 44,
				gridGap: 3,
				poolW: hasImages ? 100 : 0,
				poolMinW: hasImages ? 90 : 0,
				poolMaxW: hasImages ? 120 : 0,
				rowGap: 2,
				rowPad: "2px 4px",
				textLineHeight: 1.15,
			}
			: matchDensity === "compact"
			? {
				slotMinH: hasImages ? 72 : 0,
				slotImg: 52,
				leftImg: 56,
				poolImg: 52,
				gridGap: hasImages ? 6 : 4,
				poolW: hasImages ? 112 : 0,
				poolMinW: hasImages ? 100 : 120,
				poolMaxW: hasImages ? 132 : 168,
				rowGap: 4,
				rowPad: "4px 6px",
				textLineHeight: 1.25,
			}
			: {
				slotMinH: hasImages ? 100 : 0,
				slotImg: 68,
				leftImg: 72,
				poolImg: 72,
				gridGap: hasImages ? 8 : 6,
				poolW: hasImages ? 128 : 0,
				poolMinW: hasImages ? 118 : 128,
				poolMaxW: hasImages ? 148 : 180,
				rowGap: 6,
				rowPad: "6px 8px",
				textLineHeight: 1.3,
			};

	useEffect(() => {
		if (done && answer?.pairs?.length) {
			const obj = {};
			answer.pairs.forEach(([l, r]) => {
				obj[l] = r;
			});
			setAssignments(obj);
			return;
		}
		if (!done) setAssignments({});
	}, [done, answer?.pairs, firstQ?.matchLeft, firstQ?.matchRight]);

	useEffect(() => {
		if (done) setTapPickRightId(null);
	}, [done]);

	useEffect(() => {
		ensureMatchDragStyles();
	}, []);

	useEffect(() => {
		if (typeof document === "undefined") return undefined;
		if (isDraggingMatch && !done) {
			document.body.setAttribute("data-er-match-drag", "1");
		} else {
			document.body.removeAttribute("data-er-match-drag");
		}
		return () => document.body.removeAttribute("data-er-match-drag");
	}, [isDraggingMatch, done]);

	const beginMatchDrag = () => setIsDraggingMatch(true);

	const getRightById = (id) => rightItems.find((r) => r.id === id);
	const assignedRightIds = Object.values(assignments).filter(Boolean);
	const canCheck = leftItems.length > 0 && leftItems.every((l) => assignments[l.id]);

	const textSlotMinH = 0;
	const textRowMinH = 0;

	const compactMatchFontSize = useMemo(() => {
		const raw = optionTextStyle?.fontSize;
		if (typeof raw === "number" && Number.isFinite(raw)) return Math.max(9, Math.round(raw * 0.82));
		if (typeof raw === "string") {
			const m = /^(\d+(?:\.\d+)?)px$/i.exec(raw.trim());
			if (m) return `${Math.max(9, Math.round(parseFloat(m[1]) * 0.82))}px`;
		}
		return textUltraCompact ? 10 : 11;
	}, [optionTextStyle?.fontSize, textUltraCompact]);

	const slotBg = (right) => {
		if (!right) return "#f1f5f9";
		return matchHasText(right) ? "rgba(255, 255, 255, 0.92)" : "transparent";
	};

	const slotDropHighlight = (active, right) => ({
		border: `2px dashed ${active ? "#7c3aed" : right ? "#94a3b8" : "#64748b"}`,
		background: active ? "rgba(124, 58, 237, 0.14)" : slotBg(right),
		boxShadow: active ? "0 0 0 2px rgba(124, 58, 237, 0.22)" : "none",
	});

	const clearDragOverIfLeaving = (e, leftId) => {
		const rel = e.relatedTarget;
		if (rel && e.currentTarget.contains(rel)) return;
		setDragOverLeftId((x) => (x === leftId ? null : x));
	};

	const handleDragStart = (e, rightId) => {
		if (done) return;
		beginMatchDrag();
		e.dataTransfer.setData("application/json", JSON.stringify({ type: "right", id: rightId }));
		e.dataTransfer.effectAllowed = "move";
		const r = getRightById(rightId);
		const ne = e.nativeEvent;
		const ax = ne && typeof ne.offsetX === "number" ? ne.offsetX : 12;
		const ay = ne && typeof ne.offsetY === "number" ? ne.offsetY : 12;
		setPlainTextDragImage(e.dataTransfer, r?.text || "—", ax, ay);
	};
	const handleSlotDragStart = (e, leftId, rightId) => {
		if (!rightId || done) return;
		beginMatchDrag();
		e.dataTransfer.setData("application/json", JSON.stringify({ type: "fromSlot", leftId, rightId }));
		e.dataTransfer.effectAllowed = "move";
		const r = getRightById(rightId);
		const ne = e.nativeEvent;
		const ax = ne && typeof ne.offsetX === "number" ? ne.offsetX : 12;
		const ay = ne && typeof ne.offsetY === "number" ? ne.offsetY : 12;
		setPlainTextDragImage(e.dataTransfer, r?.text || "—", ax, ay);
	};
	const handleDragOver = (e, leftId) => {
		if (done) return;
		e.preventDefault();
		e.stopPropagation();
		e.dataTransfer.dropEffect = "move";
		if (leftId) setDragOverLeftId(leftId);
	};
	const handleDropOnSlot = (e, leftId) => {
		if (done) return;
		e.preventDefault();
		setDragOverLeftId(null);
		setIsDraggingMatch(false);
		try {
			const d = JSON.parse(e.dataTransfer.getData("application/json") || "{}");
			if (d.type === "right") {
				setAssignments((prev) => ({ ...prev, [leftId]: d.id }));
			} else if (d.type === "fromSlot") {
				setAssignments((prev) => {
					const next = { ...prev };
					delete next[d.leftId];
					next[leftId] = d.rightId;
					return next;
				});
			}
		} catch (_) {}
	};
	const handleDropOnPool = (e) => {
		if (done) return;
		e.preventDefault();
		setDragOverLeftId(null);
		setIsDraggingMatch(false);
		try {
			const d = JSON.parse(e.dataTransfer.getData("application/json") || "{}");
			if (d.type === "fromSlot") {
				setAssignments((prev) => {
					const next = { ...prev };
					delete next[d.leftId];
					return next;
				});
			}
		} catch (_) {}
	};

	useEffect(() => {
		if (!isDraggingMatch || done) return undefined;
		const prevent = (e) => {
			e.preventDefault();
			if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
		};
		const trackSlot = (e) => {
			prevent(e);
			const target = resolveMatchDropTarget(e.clientX, e.clientY);
			setDragOverLeftId(target?.kind === "slot" ? target.leftId : null);
		};
		const handleGlobalDrop = (e) => {
			const target = resolveMatchDropTarget(e.clientX, e.clientY);
			if (target?.kind === "slot") {
				handleDropOnSlot(e, target.leftId);
			} else if (target?.kind === "pool") {
				handleDropOnPool(e);
			}
			setIsDraggingMatch(false);
			setDragOverLeftId(null);
		};
		const endDrag = () => {
			setIsDraggingMatch(false);
			setDragOverLeftId(null);
		};
		window.addEventListener("dragover", trackSlot);
		window.addEventListener("drop", handleGlobalDrop);
		window.addEventListener("dragend", endDrag);
		return () => {
			window.removeEventListener("dragover", trackSlot);
			window.removeEventListener("drop", handleGlobalDrop);
			window.removeEventListener("dragend", endDrag);
		};
	}, [isDraggingMatch, done]);

	const handleCheck = () => {
		if (!canCheck || !onConfirm) return;
		const pairs = leftItems
			.map((l) => [l.id, assignments[l.id]])
			.filter(([, r]) => !!r);
		onConfirm({ pairs });
	};

	const handleSlotTap = (leftId, currentRightId) => {
		if (done) return;
		if (tapPickRightId) {
			setAssignments((prev) => ({ ...prev, [leftId]: tapPickRightId }));
			setTapPickRightId(null);
			return;
		}
		if (currentRightId) {
			setAssignments((prev) => {
				const next = { ...prev };
				delete next[leftId];
				return next;
			});
		}
	};

	const handlePoolChipTap = (rightId) => {
		if (done) return;
		setTapPickRightId((prev) => (prev === rightId ? null : rightId));
	};

	const accentTitle = "#14b8a6";
	const cardBorder = "1px solid rgba(100, 116, 139, 0.4)";
	const imgErr = (e) => {
		e.target.style.display = "none";
	};

	/** Contenido visual de un ítem (icono grande + texto con fondo solo si hay texto). */
	const renderMatchFace = (item, { bracketLabel, imgMaxH = 88, minImgBox = 0, compact = false }) => {
		const imgUrl = matchResolvedImg(item);
		const hasImg = !!imgUrl;
		const brTok = bracketLabel ? String(bracketLabel).trim() : "";
		const raw = matchTrim(item?.text);
		let { title, subtitle } = splitMatchText(item?.text);
		if (brTok && matchTrim(title) === brTok) title = "";
		const onlyAutoBracket = !!(brTok && raw === brTok && !hasImg);
		const showTextCard = matchHasText(item) && !onlyAutoBracket && (!!matchTrim(title) || !!matchTrim(subtitle));
		const imgWrapStyle = {
			width: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			padding: "2px 0",
			boxSizing: "border-box",
			...(minImgBox > 0 ? { minHeight: minImgBox } : {}),
		};
		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 4,
					width: "100%",
					minWidth: 0,
					boxSizing: "border-box",
				}}
			>
				{hasImg ? (
					<div style={imgWrapStyle}>
						<img
							src={imgUrl}
							alt=""
							draggable={false}
							onError={imgErr}
							style={{
								maxWidth: "100%",
								maxHeight: imgMaxH,
								width: "auto",
								height: "auto",
								objectFit: "contain",
								display: "block",
							}}
						/>
					</div>
				) : null}
				{showTextCard ? (
					<div
						style={{
							width: "100%",
							padding: compact ? "4px 6px" : "6px 8px",
							borderRadius: 8,
							background: "rgba(248, 250, 252, 0.95)",
							border: "1px solid rgba(226, 232, 240, 0.9)",
							boxSizing: "border-box",
						}}
					>
						{matchTrim(subtitle) ? (
							matchTrim(title) ? (
								<>
									<div
										style={{
											fontWeight: 800,
											letterSpacing: "0.06em",
											textTransform: "uppercase",
											fontSize: compact ? "0.76em" : "0.82em",
											color: accentTitle,
											textAlign: "center",
											lineHeight: 1.2,
										}}
									>
										{title}
									</div>
									<div
										style={{
											marginTop: compact ? 4 : 6,
											fontSize: compact ? "0.8em" : "0.88em",
											color: "#475569",
											textAlign: "center",
											lineHeight: 1.35,
											...optionTextStyle,
											fontWeight: optionTextStyle?.fontWeight ?? 400,
										}}
									>
										{subtitle}
									</div>
								</>
							) : (
								<div
									style={{
										fontSize: "0.88em",
										color: "#475569",
										textAlign: "center",
										lineHeight: 1.35,
										...optionTextStyle,
										fontWeight: optionTextStyle?.fontWeight ?? 400,
									}}
								>
									{subtitle}
								</div>
							)
						) : (
							<div
								style={{
									fontWeight: 600,
									textAlign: "center",
									lineHeight: 1.35,
									...optionTextStyle,
								}}
							>
								{title}
							</div>
						)}
					</div>
				) : null}
				{!hasImg && !showTextCard && !bracketLabel ? <span style={{ color: "#94a3b8" }}>—</span> : null}
				{bracketLabel ? (
					<div style={{ fontSize: 12, fontWeight: 700, color: accentTitle, letterSpacing: "0.08em" }}>{bracketLabel}</div>
				) : null}
			</div>
		);
	};

	const poolShellStyle = (r, { grid }) => {
		const hasTx = matchHasText(r);
		const picked = tapPickRightId === r.id;
		return {
			borderRadius: grid ? 10 : 8,
			border: picked ? "2px solid #7c3aed" : "1px solid rgba(148, 163, 184, 0.55)",
			background: hasTx ? "rgba(248, 250, 252, 0.95)" : "transparent",
			cursor: "grab",
			touchAction: "manipulation",
			WebkitUserSelect: "none",
			userSelect: "none",
			boxShadow: picked ? "0 0 0 3px rgba(124, 58, 237, 0.25)" : undefined,
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			gap: 4,
			padding: grid ? "8px 8px 6px" : "8px 12px",
			flex: grid ? "0 0 auto" : undefined,
			width: grid ? densitySizes.poolW : undefined,
			minWidth: grid ? densitySizes.poolMinW : undefined,
			maxWidth: grid ? densitySizes.poolMaxW : undefined,
			overflow: grid ? "hidden" : undefined,
			boxSizing: "border-box",
		};
	};

	const isTextGrid = !hasImages;

	const textMatchBodyStyle = {
		display: "block",
		width: "100%",
		minWidth: 0,
		textAlign: "center",
		lineHeight: densitySizes.textLineHeight,
		wordBreak: "break-word",
		overflowWrap: "anywhere",
		...optionTextStyle,
		fontSize: compactMatchFontSize,
	};

	const textClampStyle = {
		display: "-webkit-box",
		WebkitLineClamp: 2,
		WebkitBoxOrient: "vertical",
		overflow: "hidden",
	};

	const renderTextPairCell = (left, slotActive, right) => (
		<>
			<div
				style={{
					flex: "0 0 auto",
					minWidth: 0,
					borderRadius: 6,
					border: cardBorder,
					background: matchItemFillBg(left),
					padding: densitySizes.rowPad,
					boxSizing: "border-box",
					overflow: "hidden",
				}}
			>
				<span style={{ ...textMatchBodyStyle, fontWeight: optionTextStyle?.fontWeight ?? 600, ...textClampStyle }}>
					{matchHasText(left) ? left.text : "—"}
				</span>
			</div>
			<div
				data-match-slot-id={left.id}
				role={done ? undefined : "button"}
				tabIndex={done ? undefined : 0}
				onKeyDown={
					done
						? undefined
						: (e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								handleSlotTap(left.id, right?.id);
							}
						}
				}
				onClick={() => handleSlotTap(left.id, right?.id)}
				onDragEnter={(e) => handleDragOver(e, left.id)}
				onDragOver={(e) => handleDragOver(e, left.id)}
				onDragLeave={(e) => clearDragOverIfLeaving(e, left.id)}
				onDrop={(e) => handleDropOnSlot(e, left.id)}
				draggable={!touchPreferTap && !!right && !done}
				onDragStart={!touchPreferTap && right ? (e) => handleSlotDragStart(e, left.id, right.id) : undefined}
				style={{
					flex: "1 1 0",
					minWidth: 0,
					minHeight: 0,
					padding: densitySizes.rowPad,
					overflow: "hidden",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					borderRadius: 6,
					...slotDropHighlight(slotActive, right),
					cursor: done ? "default" : tapPickRightId && !right ? "pointer" : right ? "grab" : "pointer",
					touchAction: "manipulation",
					WebkitUserSelect: "none",
					userSelect: "none",
					boxSizing: "border-box",
				}}
			>
				{right ? (
					<span style={{ ...textMatchBodyStyle, ...textClampStyle }}>{matchHasText(right) ? right.text : "—"}</span>
				) : (
					<span style={{ color: "#94a3b8", fontSize: textUltraCompact ? 9 : 10, fontWeight: 600, textAlign: "center" }}>Suelta aquí</span>
				)}
			</div>
		</>
	);

	return (
		<div
			style={{
				flex: 1,
				display: "flex",
				flexDirection: "column",
				gap: textUltraCompact ? 3 : 4,
				minHeight: 0,
				height: "100%",
				overflow: "hidden",
				position: "relative",
				boxSizing: "border-box",
			}}
		>
			<div
				style={{
					flex: useTextPairGrid ? "3 1 0" : "1 1 0",
					minHeight: 0,
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
				}}
			>
				{useGridLayout ? (
					<div
						style={{
							flex: isTextGrid ? "0 0 auto" : hasImages ? 1 : "0 1 auto",
							minHeight: isTextGrid ? undefined : 0,
							display: "grid",
							gridTemplateColumns:
								!hasImages && itemCount >= 4
									? "repeat(2, minmax(0, 1fr))"
									: "repeat(auto-fit, minmax(min(100%, 120px), 1fr))",
							gridAutoRows: "auto",
							gap: densitySizes.gridGap,
							alignItems: "stretch",
							alignContent: "start",
						}}
					>
						{leftItems.map((left, idx) => {
							const rightId = assignments[left.id];
							const right = rightId ? getRightById(rightId) : null;
							const slotActive = dragOverLeftId === left.id || (tapPickRightId && !right);
							const labelBlock = (
								<div
									style={{
										flex: "0 0 auto",
										minWidth: 0,
										borderRadius: 10,
										border: cardBorder,
										background: hasImages ? "transparent" : matchItemFillBg(left),
										padding: densitySizes.rowPad,
										boxSizing: "border-box",
										overflow: "hidden",
									}}
								>
									{hasImages ? (
										renderMatchFace(left, { bracketLabel: `[${idx + 1}]`, imgMaxH: densitySizes.leftImg, minImgBox: 0, compact: matchDensity === "compact" })
									) : (
										<span style={{ ...textMatchBodyStyle, fontWeight: optionTextStyle?.fontWeight ?? 600 }}>{matchHasText(left) ? left.text : "—"}</span>
									)}
								</div>
							);
							const slotBlock = (
								<div
									data-match-slot-id={left.id}
									role={done ? undefined : "button"}
									tabIndex={done ? undefined : 0}
									onKeyDown={
										done
											? undefined
											: (e) => {
												if (e.key === "Enter" || e.key === " ") {
													e.preventDefault();
													handleSlotTap(left.id, right?.id);
												}
											}
									}
									onClick={() => handleSlotTap(left.id, right?.id)}
									onDragEnter={(e) => handleDragOver(e, left.id)}
									onDragOver={(e) => handleDragOver(e, left.id)}
									onDragLeave={(e) => clearDragOverIfLeaving(e, left.id)}
									onDrop={(e) => handleDropOnSlot(e, left.id)}
									draggable={!touchPreferTap && !!right && !done}
									onDragStart={!touchPreferTap && right ? (e) => handleSlotDragStart(e, left.id, right.id) : undefined}
									style={{
										flex: hasImages ? "0 0 auto" : "0 0 auto",
										minWidth: 0,
										minHeight: hasImages ? densitySizes.slotMinH : textSlotMinH,
										padding: densitySizes.rowPad,
										overflow: "hidden",
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "center",
										borderRadius: 10,
										...slotDropHighlight(slotActive, right),
										cursor: done ? "default" : tapPickRightId && !right ? "pointer" : right ? "grab" : "pointer",
										touchAction: "manipulation",
										WebkitUserSelect: "none",
										userSelect: "none",
										boxSizing: "border-box",
										transition: "border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease",
									}}
								>
									{right ? (
										hasImages ? (
											<div style={{ width: "100%", minWidth: 0 }}>{renderMatchFace(right, { imgMaxH: densitySizes.slotImg, minImgBox: 0, compact: matchDensity === "compact" })}</div>
										) : (
											<span style={textMatchBodyStyle}>{matchHasText(right) ? right.text : "—"}</span>
										)
									) : (
										<span style={{ color: "#94a3b8", fontSize: 11, fontWeight: 600, textAlign: "center" }}>Suelta aquí</span>
									)}
								</div>
							);
							return (
								<div
									key={left.id}
									style={{
										display: "flex",
										flexDirection: "column",
										alignItems: "stretch",
										gap: hasImages ? 6 : 4,
										minWidth: 0,
										boxSizing: "border-box",
									}}
								>
									{hasImages ? (
										<>
											{slotBlock}
											{labelBlock}
										</>
									) : (
										<>
											{labelBlock}
											{slotBlock}
										</>
									)}
								</div>
							);
						})}
					</div>
				) : useTextPairGrid ? (
					<div
						style={{
							flex: 1,
							minHeight: 0,
							height: "100%",
							display: "grid",
							gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
							gridTemplateRows: `repeat(${pairGridRows}, minmax(0, 1fr))`,
							gap: densitySizes.gridGap,
							alignItems: "stretch",
						}}
					>
						{leftItems.map((left) => {
							const rightId = assignments[left.id];
							const right = rightId ? getRightById(rightId) : null;
							const slotActive = dragOverLeftId === left.id || (tapPickRightId && !right);
							return (
								<div
									key={left.id}
									style={{
										display: "flex",
										flexDirection: "column",
										gap: 2,
										minWidth: 0,
										minHeight: 0,
										overflow: "hidden",
										boxSizing: "border-box",
									}}
								>
									{renderTextPairCell(left, slotActive, right)}
								</div>
							);
						})}
					</div>
				) : (
				<div style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", gap: densitySizes.rowGap }}>
					{leftItems.map((left) => {
						const rightId = assignments[left.id];
						const right = rightId ? getRightById(rightId) : null;
						const imgL = matchResolvedImg(left);
						const slotActive = dragOverLeftId === left.id || (tapPickRightId && !right);
						return (
							<div key={left.id} style={{ display: "flex", alignItems: "stretch", gap: 6, flex: "1 1 0", minHeight: 0 }}>
								<div
									style={{
										flex: "1 1 50%",
										padding: densitySizes.rowPad,
										background: matchItemFillBg(left),
										borderRadius: 8,
										border: cardBorder,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: 8,
										minWidth: 0,
										boxSizing: "border-box",
										overflow: "hidden",
									}}
								>
									{imgL ? (
										<img
											src={imgL}
											alt=""
											draggable={false}
											onError={imgErr}
											style={{ width: 40, height: 40, flexShrink: 0, objectFit: "contain", borderRadius: 6 }}
										/>
									) : null}
									<span style={{ ...textMatchBodyStyle, fontWeight: optionTextStyle?.fontWeight ?? 600, textAlign: "center" }}>
										{matchHasText(left) ? left.text : imgL ? "" : "—"}
									</span>
								</div>
								<div
									data-match-slot-id={left.id}
									role={done ? undefined : "button"}
									tabIndex={done ? undefined : 0}
									onKeyDown={
										done
											? undefined
											: (e) => {
												if (e.key === "Enter" || e.key === " ") {
													e.preventDefault();
													handleSlotTap(left.id, right?.id);
												}
											}
									}
									onClick={() => handleSlotTap(left.id, right?.id)}
									onDragEnter={(e) => handleDragOver(e, left.id)}
									onDragOver={(e) => handleDragOver(e, left.id)}
									onDragLeave={(e) => clearDragOverIfLeaving(e, left.id)}
									onDrop={(e) => handleDropOnSlot(e, left.id)}
									draggable={!touchPreferTap && !!right && !done}
									onDragStart={!touchPreferTap && right ? (e) => handleSlotDragStart(e, left.id, right.id) : undefined}
									style={{
										flex: "1 1 50%",
										minHeight: textSlotMinH,
										padding: densitySizes.rowPad,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										borderRadius: 8,
										...slotDropHighlight(slotActive, right),
										cursor: done ? "default" : tapPickRightId && !right ? "pointer" : right ? "grab" : "pointer",
										touchAction: "manipulation",
										WebkitUserSelect: "none",
										userSelect: "none",
										overflow: "hidden",
										boxSizing: "border-box",
										transition: "border-color 0.15s ease, background 0.15s ease",
										minWidth: 0,
									}}
								>
									{right ? (
										<>
											{matchResolvedImg(right) ? (
												<img
													src={matchResolvedImg(right)}
													alt=""
													draggable={false}
													onError={imgErr}
													style={{ width: 36, height: 36, flexShrink: 0, objectFit: "contain", borderRadius: 6, marginRight: 6 }}
												/>
											) : null}
											<span style={textMatchBodyStyle}>{matchHasText(right) ? right.text : matchResolvedImg(right) ? "" : "—"}</span>
										</>
									) : (
										<span style={{ color: "#94a3b8", fontSize: 11, fontWeight: 600 }}>Suelta aquí</span>
									)}
								</div>
							</div>
						);
					})}
				</div>
			)}
			</div>

			{!done && (
				<div
					data-match-pool
					onDragOver={(e) => handleDragOver(e)}
					onDrop={handleDropOnPool}
					style={{
						flex: useTextPairGrid ? "2 1 0" : "0 0 auto",
						minHeight: 0,
						overflow: "hidden",
						position: "relative",
						zIndex: 10,
						padding: textUltraCompact ? "3px 4px" : "6px",
						borderRadius: 8,
						border: "1px dashed #cbd5e1",
						background: "rgba(255, 255, 255, 0.97)",
						display: "flex",
						flexDirection: "column",
						gap: textUltraCompact ? 2 : 4,
						pointerEvents: "auto",
						isolation: "isolate",
						boxSizing: "border-box",
					}}
				>
					{!textUltraCompact ? (
						<div style={{ fontSize: 9, color: "#94a3b8", lineHeight: 1.1, flexShrink: 0 }}>Arrastra estas opciones:</div>
					) : null}
					<div
						style={{
							flex: 1,
							minHeight: 0,
							display: "grid",
							gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
							gridAutoRows: "minmax(0, 1fr)",
							gap: densitySizes.gridGap,
							width: "100%",
							overflow: "hidden",
						}}
					>
					{rightItems
						.filter((r) => !assignedRightIds.includes(r.id))
						.map((r) => (
							<div
								key={r.id}
								data-match-pool-chip
								draggable={!touchPreferTap}
								onDragStart={!touchPreferTap ? (e) => { e.stopPropagation(); handleDragStart(e, r.id); } : undefined}
								onDragEnd={() => { setIsDraggingMatch(false); setDragOverLeftId(null); }}
								onClick={() => handlePoolChipTap(r.id)}
								style={
									useGridLayout
										? poolShellStyle(r, { grid: true })
										: {
											display: "flex",
											alignItems: "center",
											gap: 4,
											padding: textUltraCompact ? "3px 5px" : "5px 8px",
											borderRadius: 6,
											border: tapPickRightId === r.id ? "2px solid #7c3aed" : "1px solid #e2e8f0",
											background: "#fff",
											cursor: "grab",
											touchAction: "manipulation",
											WebkitUserSelect: "none",
											userSelect: "none",
											boxShadow: tapPickRightId === r.id ? "0 0 0 2px rgba(124, 58, 237, 0.22)" : "none",
											minWidth: 0,
											minHeight: 0,
											height: "100%",
											overflow: "hidden",
											boxSizing: "border-box",
											position: "relative",
											zIndex: 2,
											...optionTextStyle,
											fontSize: compactMatchFontSize,
										}
								}
								title={intl.formatMessage({ id: "QUIZ.DRAG_SLOT" })}
							>
								{useGridLayout ? (
									<>
										<div style={{ width: "100%", minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
											{renderMatchFace(r, { imgMaxH: densitySizes.poolImg, minImgBox: 0, compact: matchDensity === "compact" })}
										</div>
										<span className="material-icons" aria-hidden style={{ fontSize: 16, color: "#94a3b8", flexShrink: 0 }}>
											drag_indicator
										</span>
									</>
								) : (
									<>
										<span style={{ flex: 1, minWidth: 0, lineHeight: densitySizes.textLineHeight, wordBreak: "break-word", overflowWrap: "anywhere", fontSize: compactMatchFontSize, ...textClampStyle }}>{r.text || "—"}</span>
										<span className="material-icons" aria-hidden style={{ fontSize: textUltraCompact ? 12 : 14, color: "#94a3b8", flexShrink: 0 }}>
											drag_indicator
										</span>
									</>
								)}
							</div>
						))}
					</div>
				</div>
			)}
			{!done && (
				<button
					type="button"
					onClick={handleCheck}
					disabled={!canCheck}
					style={{
						flexShrink: 0,
						alignSelf: "flex-start",
						padding: textUltraCompact ? "4px 12px" : "6px 14px",
						borderRadius: 6,
						border: "none",
						background: canCheck ? "rgba(124, 58, 237, 1)" : "#cbd5e1",
						color: "#fff",
						cursor: canCheck ? "pointer" : "not-allowed",
						fontWeight: 600,
						fontSize: textUltraCompact ? 11 : 12,
					}}
				>
					Comprobar
				</button>
			)}
		</div>
	);
}

function derivedFillOptions(segments = []) {
	return (segments || [])
		.filter((s) => s?.type === "blank")
		.map((s) => String(s?.correctAnswer || "").trim())
		.filter(Boolean);
}

export function FillBlanksQuizPlay({ firstQ, onConfirm, done, answer, optionTextStyle }) {
	const intl = useIntl();
	const touchPreferTap = useTouchPreferTapOverDrag();
	const segments = firstQ?.segments || [];
	const blanks = segments.filter((s) => s?.type === "blank");
	const [blankAnswers, setBlankAnswers] = useState({});
	/** Toca opción → toca hueco (complemento al drag en móvil). */
	const [tapPickValue, setTapPickValue] = useState(null);
	const options = (firstQ?.fillOptions && firstQ.fillOptions.length > 0)
		? firstQ.fillOptions
		: derivedFillOptions(segments);

	useEffect(() => {
		if (done && Array.isArray(answer?.blanks)) {
			const obj = {};
			answer.blanks.forEach((b) => { obj[b.blankId] = b.answer; });
			setBlankAnswers(obj);
			return;
		}
		if (!done) setBlankAnswers({});
	}, [done, answer?.blanks, firstQ?.segments, firstQ?.fillOptions]);

	useEffect(() => {
		if (done) setTapPickValue(null);
	}, [done]);

	const handleOptionDragStart = (e, value) => {
		if (done) return;
		e.dataTransfer.setData("application/json", JSON.stringify({ type: "fillOption", value }));
		e.dataTransfer.effectAllowed = "move";
		const ne = e.nativeEvent;
		const ax = ne && typeof ne.offsetX === "number" ? ne.offsetX : 12;
		const ay = ne && typeof ne.offsetY === "number" ? ne.offsetY : 12;
		setPlainTextDragImage(e.dataTransfer, value, ax, ay);
	};

	const handleBlankDragStart = (e, blankId) => {
		if (done) return;
		const val = blankAnswers[blankId];
		if (!val) return;
		e.dataTransfer.setData("application/json", JSON.stringify({ type: "fromBlank", blankId, value: val }));
		e.dataTransfer.effectAllowed = "move";
		const ne = e.nativeEvent;
		const ax = ne && typeof ne.offsetX === "number" ? ne.offsetX : 12;
		const ay = ne && typeof ne.offsetY === "number" ? ne.offsetY : 12;
		setPlainTextDragImage(e.dataTransfer, val, ax, ay);
	};

	const handleDragOver = (e) => {
		if (done) return;
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
	};

	const handleBlankDrop = (e, blankId) => {
		if (done) return;
		e.preventDefault();
		try {
			const d = JSON.parse(e.dataTransfer.getData("application/json") || "{}");
			if (d.type === "fillOption") {
				setBlankAnswers((prev) => ({ ...prev, [blankId]: String(d.value || "") }));
			} else if (d.type === "fromBlank") {
				setBlankAnswers((prev) => {
					const next = { ...prev };
					delete next[d.blankId];
					next[blankId] = String(d.value || "");
					return next;
				});
			}
		} catch (_) {}
	};
	const handlePoolDrop = (e) => {
		if (done) return;
		e.preventDefault();
		try {
			const d = JSON.parse(e.dataTransfer.getData("application/json") || "{}");
			if (d.type === "fromBlank") {
				setBlankAnswers((prev) => {
					const next = { ...prev };
					delete next[d.blankId];
					return next;
				});
			}
		} catch (_) {}
	};
	const assignedValues = Object.values(blankAnswers).filter(Boolean);
	const availableOptions = options.filter((opt) => !assignedValues.includes(opt));
	const filledCount = blanks.filter((b) => blankAnswers[b.blankId]).length;
	const canCheck = blanks.length > 0 && filledCount === blanks.length;
	const correctBlanks = new Set();
	if (done && Array.isArray(answer?.blanks)) {
		answer.blanks.forEach((b) => {
			const seg = blanks.find((s) => s.blankId === b.blankId);
			if (seg && String((seg.correctAnswer || "").trim()).toLowerCase() === String((b.answer || "").trim()).toLowerCase()) {
				correctBlanks.add(b.blankId);
			}
		});
	}

	const handleBlankTap = (blankId, currentVal) => {
		if (done) return;
		if (tapPickValue != null && tapPickValue !== "") {
			setBlankAnswers((prev) => ({ ...prev, [blankId]: String(tapPickValue) }));
			setTapPickValue(null);
			return;
		}
		if (currentVal) {
			setBlankAnswers((prev) => {
				const next = { ...prev };
				delete next[blankId];
				return next;
			});
		}
	};

	const handlePoolOptionTap = (opt) => {
		if (done) return;
		setTapPickValue((prev) => (prev === opt ? null : opt));
	};

	return (
		<div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10, minHeight: 0, overflow: "hidden" }}>
			{!done && options.length > 0 ? (
				<div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.35 }}>
					Arrastra cada opción a un hueco, o <strong>toca</strong> una ficha y luego el hueco.
				</div>
			) : null}
			<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4, ...optionTextStyle, lineHeight: 1.6 }}>
				{segments.map((seg, i) => {
					if (seg.type === "text") {
						return <span key={seg.segmentId || `txt-${i}`}>{seg.value || ""}</span>;
					}
					const blankId = seg.blankId || `blank-${i}`;
					const val = blankAnswers[blankId];
					return (
						<div
							key={blankId}
							role={done ? undefined : "button"}
							tabIndex={done ? undefined : 0}
							onKeyDown={
								done
									? undefined
									: (e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
											handleBlankTap(blankId, val);
										}
									}
							}
							onClick={() => handleBlankTap(blankId, val)}
							onDragOver={handleDragOver}
							onDrop={(e) => handleBlankDrop(e, blankId)}
							onDragStart={!touchPreferTap && blankAnswers[blankId] ? (e) => handleBlankDragStart(e, blankId) : undefined}
							draggable={!touchPreferTap && !!blankAnswers[blankId] && !done}
							style={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								minWidth: 92,
								padding: "6px 10px",
								background: blankAnswers[blankId] ? "#fff" : "#f1f5f9",
								borderRadius: 6,
								border: `2px dashed ${
									blankAnswers[blankId] ? "#94a3b8" : tapPickValue != null ? "#7c3aed" : "#cbd5e1"
								}`,
								color: blankAnswers[blankId] ? "#334155" : "#94a3b8",
								cursor: !done ? "pointer" : "default",
								touchAction: "manipulation",
								WebkitUserSelect: "none",
								userSelect: "none",
								borderColor: done && correctBlanks.has(blankId)
									? "#22c55e"
									: done && blankAnswers[blankId] && !correctBlanks.has(blankId)
										? "#ef4444"
										: undefined,
								...optionTextStyle,
							}}
						>
							{blankAnswers[blankId] || "____"}
						</div>
					);
				})}
			</div>
			{!done && (
				<div onDragOver={handleDragOver} onDrop={handlePoolDrop} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
					<div style={{ fontSize: 11, color: "#94a3b8" }}>Arrastra cada opción a un hueco (o usa toque: ficha → hueco):</div>
					<div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
					{availableOptions.map((opt, i) => (
						<div
							key={`${opt}-${i}`}
							draggable={!touchPreferTap}
							onDragStart={
								!touchPreferTap
									? (e) => {
										const el = e.currentTarget;
										el.dataset.erDragging = "1";
										const clear = () => {
											delete el.dataset.erDragging;
											window.removeEventListener("dragend", clear);
										};
										window.addEventListener("dragend", clear);
										handleOptionDragStart(e, opt);
									}
									: undefined
							}
							onClick={() => handlePoolOptionTap(opt)}
							onMouseEnter={(e) => {
								if (e.currentTarget.dataset.erDragging) return;
								e.currentTarget.style.borderColor = "#cbd5e1";
								e.currentTarget.style.boxShadow = "0 4px 12px rgba(15, 23, 42, 0.12)";
								e.currentTarget.style.transform = "translateY(-1px)";
							}}
							onMouseLeave={(e) => {
								if (e.currentTarget.dataset.erDragging) return;
								e.currentTarget.style.borderColor = tapPickValue === opt ? "#7c3aed" : "#e2e8f0";
								e.currentTarget.style.boxShadow = tapPickValue === opt ? "0 0 0 3px rgba(124, 58, 237, 0.25)" : "0 1px 2px rgba(0,0,0,0.05)";
								e.currentTarget.style.transform = "translateY(0)";
							}}
							style={{
								display: "flex",
								alignItems: "center",
								gap: 6,
								padding: "8px 14px",
								background: "#fff",
								borderRadius: 8,
								border: tapPickValue === opt ? "2px solid #7c3aed" : "1px solid #e2e8f0",
								cursor: "grab",
								boxShadow: tapPickValue === opt ? "0 0 0 3px rgba(124, 58, 237, 0.25)" : "0 1px 2px rgba(0,0,0,0.05)",
								color: "#1e293b",
								fontWeight: 600,
								fontSize: (optionTextStyle.fontSize || 12) * 0.95,
								transition: "all 0.15s ease",
								touchAction: "manipulation",
								WebkitUserSelect: "none",
								userSelect: "none",
							}}
							title={intl.formatMessage({ id: "QUIZ.DRAG_GAP" })}
						>
							{opt}
							<span
								className="material-icons"
								aria-hidden
								style={{ fontSize: 14, color: "#cbd5e1", flexShrink: 0 }}
							>
								drag_indicator
							</span>
						</div>
					))}
					{options.length === 0 && (
						<span style={{ fontSize: 12, color: "#94a3b8" }}>Escribe las respuestas correctas en cada hueco</span>
					)}
					</div>
				</div>
			)}
			{!done && (
				<button
					type="button"
					onClick={() => canCheck && onConfirm && onConfirm({
						blanks: blanks.map((b) => ({ blankId: b.blankId, answer: blankAnswers[b.blankId] || "" })),
						confirmed: true,
					})}
					disabled={!canCheck}
					style={{
						alignSelf: "flex-start",
						padding: "8px 16px",
						borderRadius: 8,
						border: "none",
						background: canCheck ? "rgba(124, 58, 237, 1)" : "#cbd5e1",
						color: "#fff",
						cursor: canCheck ? "pointer" : "not-allowed",
						fontWeight: 600,
					}}
				>
					Comprobar
				</button>
			)}
		</div>
	);
}

export function normalizeQuizTextAnswer(value) {
	return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

export function quizTextAnswersMatch(submitted, expected) {
	return normalizeQuizTextAnswer(submitted) === normalizeQuizTextAnswer(expected);
}

export function resolveQuizAttemptMode(quiz) {
	return quiz?.attemptMode || (quiz?.allowMultipleAttempts ? "unlimited" : "single");
}

export function resolveCustomQuizDone({ quiz, isSurvey, allCorrect, attemptCount }) {
	const attemptMode = resolveQuizAttemptMode(quiz);
	const maxAttempts = Math.max(1, Number(quiz?.maxAttempts) || 3);
	if (isSurvey) {
		if (attemptMode === "single") return true;
		if (attemptMode === "limited") return attemptCount >= maxAttempts;
		return attemptCount >= 1;
	}
	if (attemptMode === "single") return true;
	if (attemptMode === "limited") return allCorrect || attemptCount >= maxAttempts;
	return allCorrect;
}

export function accumulateCustomQuizPoints({ quiz, isSurvey, isCorrect, pointsCorrect, pointsIncorrect, previousTotal = 0 }) {
	if (isSurvey) return 0;
	const delta = isCorrect ? pointsCorrect : pointsIncorrect;
	if (resolveQuizAttemptMode(quiz) === "single") return delta;
	return previousTotal + delta;
}

export function TextAnswerQuizPlay({
	firstQ,
	onConfirm,
	done,
	answer,
	optionTextStyle,
	placeholder,
	fieldContainerStyle,
	fieldInputStyle,
	fieldClassName,
	fieldSubmitStyle,
	fieldSubmitClassName,
}) {
	const intl = useIntl();
	const [text, setText] = useState("");

	useEffect(() => {
		if (done && answer?.text != null) {
			setText(String(answer.text));
			return;
		}
		if (!done) setText("");
	}, [done, answer?.text]);

	const handleSubmit = () => {
		if (done || !onConfirm) return;
		const trimmed = String(text || "").trim();
		if (!trimmed) return;
		onConfirm({ text: trimmed });
	};

	const inputStyle = {
		...optionTextStyle,
		...fieldInputStyle,
		flex: 1,
		minWidth: 0,
		minHeight: 0,
		width: "100%",
		height: "100%",
		alignSelf: "stretch",
		border: fieldInputStyle?.border ?? "none",
		background: fieldInputStyle?.background ?? "transparent",
		outline: "none",
		boxShadow: "none",
		borderRadius: fieldInputStyle?.borderRadius ?? "inherit",
		WebkitAppearance: "none",
		appearance: "none",
		boxSizing: "border-box",
	};

	const containerStyle = {
		flex: 1,
		minHeight: 0,
		minWidth: 0,
		alignSelf: "stretch",
		display: "flex",
		alignItems: "stretch",
		boxSizing: "border-box",
		overflow: "hidden",
		...fieldContainerStyle,
	};

	const submitFontSize = optionTextStyle?.fontSize || 13;
	const canSubmit = !!String(text || "").trim();

	return (
		<div
			style={{
				flex: 1,
				minHeight: 0,
				width: "100%",
				display: "flex",
				flexDirection: "row",
				alignItems: "stretch",
				gap: 8,
				overflow: "hidden",
				boxSizing: "border-box",
			}}
			onMouseDown={(e) => e.stopPropagation()}
			onClick={(e) => e.stopPropagation()}
		>
			<div className={fieldClassName || undefined} style={containerStyle}>
				<input
					type="text"
					value={text}
					onChange={(e) => { if (!done) setText(e.target.value); }}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							handleSubmit();
						}
					}}
					disabled={done}
					placeholder={placeholder || intl.formatMessage({ id: "QUIZ.WRITE_ANSWER" })}
					style={inputStyle}
				/>
			</div>
			{!done && onConfirm ? (
				<button
					type="button"
					className={fieldSubmitClassName || undefined}
					onClick={handleSubmit}
					disabled={!canSubmit}
					style={{
						flexShrink: 0,
						alignSelf: "stretch",
						minWidth: 72,
						fontWeight: 600,
						fontSize: submitFontSize,
						cursor: canSubmit ? "pointer" : "not-allowed",
						fontFamily: optionTextStyle?.fontFamily || "inherit",
						opacity: canSubmit ? 1 : 0.55,
						WebkitAppearance: "none",
						appearance: "none",
						margin: 0,
						...fieldSubmitStyle,
					}}
				>
					Enviar
				</button>
			) : null}
		</div>
	);
}
